import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid,
  Button,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WebsiteCard from './WebsiteCard';
import SearchAndFilters from './SearchAndFilters';
import WebsiteForm from './WebsiteForm';
import ConfirmationDialog from './ConfirmationDialog';
import parseHelper from '../helpers/ParseHelper';

const WebsiteDirectory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [websites, setWebsites] = useState([]);
  const [filteredWebsites, setFilteredWebsites] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Site management state
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    logo: '',
    status: 'operational',
    tags: [],
    externalSite: false
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Fetch websites from database
  useEffect(() => {
    fetchWebsites();
  }, []);
  
  const fetchWebsites = async () => {
    setLoading(true);
    try {
      const results = await parseHelper.GetSites();
      const websiteData = results.map(site => ({
        id: site.id,
        name: site.get('siteName'),
        description: site.get('sitedescription'),
        url: site.get('siteUrl'),
        logo: site.get('siteLogo'),
        status: site.get('siteStatus'),
        tags: site.get('siteTags'),
        externalSite: site.get('externalSite'),
        parseObject: site // Store the Parse object for later use
      }));
      setWebsites(websiteData);
      setFilteredWebsites(websiteData);
    } catch (err) {
      console.error('Error fetching websites:', err);
      setError('Failed to load websites');
    } finally {
      setLoading(false);
    }
  };
  
  // Extract all unique tags from websites
  const allTags = [...new Set(websites.flatMap(site => site.tags || []))];
  
  // Filter websites based on search term, status, and tags
  useEffect(() => {
    const results = websites.filter(website => {
      // Search filter
      const matchesSearch = 
        website.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        website.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesStatus = 
        statusFilter === 'all' || 
        website.status.toLowerCase() === statusFilter.toLowerCase();
      
      // Tags filter
      const matchesTags = 
        selectedTags.length === 0 || 
        selectedTags.every(tag => website.tags && website.tags.includes(tag));
      
      return matchesSearch && matchesStatus && matchesTags;
    });
    
    setFilteredWebsites(results);
  }, [searchTerm, statusFilter, selectedTags, websites]);
  
  // Handle tag selection
  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Dialog handlers
  const handleOpenAddDialog = () => {
    setFormData({
      name: '',
      description: '',
      url: '',
      logo: '',
      status: 'operational',
      tags: [],
      externalSite: false
    });
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (website) => {
    setCurrentSite(website);
    setFormData({
      name: website.name,
      description: website.description,
      url: website.url,
      logo: website.logo,
      status: website.status,
      tags: website.tags || [],
      externalSite: website.externalSite || false
    });
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (website) => {
    setCurrentSite(website);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setCurrentSite(null);
  };

  // CRUD operations
  const handleAddWebsite = async () => {
    try {
      await parseHelper.AddNewSite(formData.name, {
        description: formData.description,
        url: formData.url,
        logo: formData.logo,
        status: formData.status,
        tags: formData.tags,
        externalSite: formData.externalSite
      });
      
      setSnackbar({
        open: true,
        message: 'Website added successfully!',
        severity: 'success'
      });
      
      handleCloseDialogs();
      fetchWebsites();
    } catch (err) {
      console.error('Error adding website:', err);
      setSnackbar({
        open: true,
        message: 'Failed to add website',
        severity: 'error'
      });
    }
  };

  const handleEditWebsite = async () => {
    try {
      const siteObj = currentSite.parseObject;
      siteObj.set('siteName', formData.name);
      siteObj.set('sitedescription', formData.description);
      siteObj.set('siteUrl', formData.url);
      siteObj.set('siteLogo', formData.logo);
      siteObj.set('siteStatus', formData.status);
      siteObj.set('siteTags', formData.tags);
      siteObj.set('externalSite', formData.externalSite);
      
      await siteObj.save();
      
      setSnackbar({
        open: true,
        message: 'Website updated successfully!',
        severity: 'success'
      });
      
      handleCloseDialogs();
      fetchWebsites();
    } catch (err) {
      console.error('Error updating website:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update website',
        severity: 'error'
      });
    }
  };

  const handleDeleteWebsite = async () => {
    try {
      const siteObj = currentSite.parseObject;
      await siteObj.destroy();
      
      setSnackbar({
        open: true,
        message: 'Website deleted successfully!',
        severity: 'success'
      });
      
      handleCloseDialogs();
      fetchWebsites();
    } catch (err) {
      console.error('Error deleting website:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete website',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h2">
          Browse Internal Websites
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={handleOpenAddDialog}
        >
          Add Website
        </Button>
      </Box>
      
      {/* Search and Filters */}
      <SearchAndFilters 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        selectedTags={selectedTags}
        handleTagToggle={handleTagToggle}
        allTags={allTags}
      />
      
      {/* Website Grid */}
      {loading ? (
        <Typography variant="h6" align="center" color="textSecondary" sx={{ my: 5 }}>
          Loading websites...
        </Typography>
      ) : error ? (
        <Typography variant="h6" align="center" color="error" sx={{ my: 5 }}>
          {error}
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredWebsites.length > 0 ? (
            filteredWebsites.map((website) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={website.id}>
                <Box sx={{ position: 'relative' }}>
                  <WebsiteCard website={website} />
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    right: 8, 
                    display: 'flex', 
                    gap: 1, 
                    backgroundColor: 'rgba(255,255,255,0.7)', 
                    borderRadius: '4px', 
                    padding: '2px'
                  }}>
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={() => handleOpenEditDialog(website)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleOpenDeleteDialog(website)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="h6" align="center" color="textSecondary" sx={{ my: 5 }}>
                No websites match your filters. Try adjusting your search criteria.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {/* Add Website Dialog */}
      <WebsiteForm
        open={openAddDialog}
        onClose={handleCloseDialogs}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleAddWebsite}
        title="Add New Website"
        submitText="Add Website"
      />

      {/* Edit Website Dialog */}
      <WebsiteForm
        open={openEditDialog}
        onClose={handleCloseDialogs}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleEditWebsite}
        title="Edit Website"
        submitText="Save Changes"
      />

      {/* Delete Website Dialog */}
      <ConfirmationDialog
        open={openDeleteDialog}
        onClose={handleCloseDialogs}
        onConfirm={handleDeleteWebsite}
        title="Delete Website"
        content={`Are you sure you want to delete "${currentSite?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
      />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WebsiteDirectory;
